import React, { useMemo, useState } from "react";

import { useUserAddresses, createUserAddress } from "../../hooks/useUserAddresses";
import { Select } from "../../components/ui";
import Window from "../../contexts/Window";
import { Button } from "../../components/ui";

import "./xero-org-select.scss";

/**
 * "Address" picker shown directly under the Xero Organisation select in the
 * PAYMENTS section of the order / sell / PX flows. Chooses which of the
 * client's saved addresses this document is billed to; the choice is pushed
 * onto the client's Xero contact when the invoice / purchase order is
 * generated, so it appears on the generated PDF.
 *
 * Deliberately borrows xero-org-select.scss so the two selects line up.
 *
 * Hidden when there is no client yet (nothing to bill to). With exactly one
 * saved address it still renders — the rep needs to see WHICH address the
 * document will carry, and "+ Add new address" is how a second one is created
 * without leaving the flow.
 */
export function XeroAddressSelect({ userId, value, onChange, disabled }) {
  const { data: addresses = [], refetch, isLoading } = useUserAddresses(userId);
  const [adding, setAdding] = useState(false);

  // Null value means "use the client's default", so show the default as
  // selected rather than an empty box that implies no address at all.
  const defaultId = useMemo(
    () => addresses.find((a) => a.is_default)?.id ?? null,
    [addresses]
  );
  const selected = value ?? defaultId;

  if (!userId) {
    return null;
  }

  const label = (a) =>
    [a.label, a.summary].filter(Boolean).join(" — ") || `Address ${a.id}`;

  return (
    <div className="xero-org-select mb-4">
      <div className="strong-label mb-2">Address:</div>
      <Select
        fullWidth
        label="Address"
        placeholder={isLoading ? "Loading…" : "No saved address"}
        options={[
          ...addresses.map((a) => ({
            value: a.id,
            label: a.is_default ? `${label(a)} (default)` : label(a),
          })),
          // An action, not an address: opens the quick form below.
          { value: "__add__", label: "+ Add new address…" },
        ]}
        value={selected == null || selected === "" ? null : Number(selected)}
        disabled={disabled || isLoading || adding}
        onChange={(v) => {
          if (v === "__add__") {
            setAdding(true);
            return;
          }
          onChange(v ?? null);
        }}
      />

      {adding && (
        <QuickAddressForm
          onCancel={() => setAdding(false)}
          onSave={async (payload) => {
            const next = await createUserAddress(userId, payload);
            await refetch();
            setAdding(false);
            // Select what was just added, so the rep doesn't have to go and
            // find it in the list straight after creating it.
            const created = next[next.length - 1];
            if (created) onChange(created.id);
          }}
        />
      )}
    </div>
  );
}

/**
 * Minimal inline address form for the PAYMENTS flow.
 *
 * Intentionally NOT the full AddressFieldset used on the Users page: that one
 * is Formik-bound and fetches per-country field formats, which is more than
 * this needs. The full editor lives on the Users page; this is the "I'm
 * mid-invoice and the address is missing" path.
 */
function QuickAddressForm({ onSave, onCancel }) {
  const [form, setForm] = useState({
    label: "", line1: "", line2: "", postal_code: "", locality: "", region: "", country_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.country_id) {
      setErr("Country is required.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSave({ ...form, country_id: Number(form.country_id) });
    } catch (e) {
      setErr(e?.response?.data?.message || "Could not save the address.");
      setSaving(false);
    }
  };

  return (
    <div className="xero-address-add mt-2">
      <input className="xero-org-select__input mb-2" placeholder="Label (e.g. Office)"
        value={form.label} onChange={set("label")} />
      <input className="xero-org-select__input mb-2" placeholder="Address line 1"
        value={form.line1} onChange={set("line1")} />
      <input className="xero-org-select__input mb-2" placeholder="Address line 2"
        value={form.line2} onChange={set("line2")} />
      <input className="xero-org-select__input mb-2" placeholder="City"
        value={form.locality} onChange={set("locality")} />
      <input className="xero-org-select__input mb-2" placeholder="Postal code"
        value={form.postal_code} onChange={set("postal_code")} />
      <CountrySelect value={form.country_id} onChange={(v) => setForm({ ...form, country_id: v })} />
      {err && <div className="xero-address-add__err">{err}</div>}
      <div className="xero-address-add__actions">
        <Button variant="secondary" data-btn="xero-cancel-address" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" data-btn="xero-save-address" onClick={save} loading={saving}>
          Save address
        </Button>
      </div>
    </div>
  );
}

/** Countries are already fetched once at app boot into the Window store, so
 *  read them from there rather than issuing another request per form. */
function CountrySelect({ value, onChange }) {
  const countries = Window.COUNTRIES || [];
  return (
    <Select
      fullWidth
      className="mb-2"
      label="Country"
      placeholder="Country"
      options={countries.map((c) => ({ value: c.id, label: c.name }))}
      value={value === "" ? null : value}
      onChange={(v) => onChange(v ?? "")}
    />
  );
}
